<template>
  <div class="mb">
    <div class="mb-head">
      <span class="mb-title">{{ title }}</span>
      <span class="mb-value" :style="{ color: barColor }">
        {{ value }}/{{ max }}
      </span>
    </div>
    <div class="mb-track">
      <div
        class="mb-fill"
        :style="{ width: pct, background: barColor, boxShadow: glowFor(barColor) }"
      />
    </div>
    <div class="mb-marks">
      <span
        v-for="m in marks"
        :key="m"
        class="mb-mark"
        :class="{ 'mb-mark-hit': value >= m }"
        :style="markStyle(m)"
      >{{ m }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { themeColor } from "@/composables/useAppConfig";

const props = defineProps<{
  title: string;
  value: number;
  max: number;
  marks: number[];
  /** Bar fill colour. Defaults to brand-green. */
  color?: "green" | "pink" | "blue";
}>();

const barColor = computed(() => {
  switch (props.color) {
    case "pink": return themeColor("--color-martech-pink", "#E94E97");   // INFINITE MarTech 粉
    case "blue": return themeColor("--color-secondary-blue", "#73D2FF");  // INFINITE Data 系次級藍
    default:     return themeColor("--color-primary-cyan", "#2BD3BF");    // INFINITE 主色薄荷霓虹青
  }
});

/** Soft halo around the fill bar matching its colour. Empty bars (value=0)
 *  have no glow so the empty track stays visually quiet. */
function glowFor(color: string): string {
  return `0 0 8px ${color}80`; // 80 = ~50% alpha in 8-digit hex
}

const pct = computed(() => {
  const p = Math.min(100, Math.max(0, (props.value / props.max) * 100));
  return `${p}%`;
});

/** Position each tick label at its real proportion of `max`, so a "30"
 *  label on a 0-36 bar sits at ~83% — not at the centre as plain
 *  flex space-between would put it. Edge ticks are anchored to the
 *  edge so the label doesn't visually clip. */
function markStyle(m: number): Record<string, string> {
  const ratio = props.max > 0 ? m / props.max : 0;
  const left = `${Math.min(100, Math.max(0, ratio * 100))}%`;
  if (m <= 0) return { left: "0%", transform: "translateX(0)" };
  if (m >= props.max) return { left: "100%", transform: "translateX(-100%)" };
  return { left, transform: "translateX(-50%)" };
}
</script>

<style scoped>
.mb { width: 100%; }

.mb-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 14px;
  color: var(--text-primary);
  margin-bottom: 8px;
}
.mb-value { font-weight: 600; }

.mb-track {
  width: 100%;
  height: 4px;
  background: var(--bg-card-2);
  border-radius: 2px;
  overflow: hidden;
}
.mb-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.4s ease;
}

.mb-marks {
  position: relative;
  height: 14px;
  margin-top: 6px;
  font-size: 11px;
  color: var(--text-mute);
}
.mb-mark {
  position: absolute;
  top: 0;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.mb-mark-hit { color: var(--text-secondary); }
</style>
